import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import ResponsiveModal from 'presentation/shared/components/ResponsiveModal'
import OpmesTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/OpmesTable'
import OpmesList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/OpmesList'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { OpmeV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { DataSource } from 'common/enum/data-source'
import { ScrollableTableContainer } from '../components/styles'
import { v4 as uuidv4 } from 'uuid'

import * as S from './styles'

type AddOpmeResponsiveModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (opmes: OpmeV2Props[]) => void
  opmes: OpmeV2Props[]
  allOpmes: OpmeV2Props[]
}

const AddOpmeResponsiveModal = ({
  isOpen,
  onClose,
  onConfirm,
  opmes,
  allOpmes
}: AddOpmeResponsiveModalProps) => {
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [addedOpmes, setAddedOpmes] = useState<OpmeV2Props[]>(opmes)

  useEffect(() => {
    if (isOpen) {
      setAddedOpmes(opmes)
    }
  }, [isOpen])

  const resetModalState = () => {
    setDescription('')
    setQuantity('1')
  }

  const handleClose = () => {
    resetModalState()
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(addedOpmes)
    resetModalState()
  }

  const handleAddOpme = () => {
    if (!description.trim() || !quantity || parseInt(quantity) <= 0) {
      toast.error('Preencha a descrição e a quantidade')
      return
    }

    const opmeExistsInExternalTable =
      allOpmes.some(
        (o) =>
          o.description.toLowerCase().trim() ===
          description.toLowerCase().trim()
      ) ||
      addedOpmes.some(
        (o) =>
          o.description.toLowerCase().trim() ===
          description.toLowerCase().trim()
      )

    if (opmeExistsInExternalTable) {
      toast.error('Este OPME já está na listagem')
      return
    }

    const newOpme: OpmeV2Props = {
      code: uuidv4(),
      description: description.trim(),
      quantity: parseInt(quantity, 10),
      source: DataSource.DOCTOR,
      is_selected: true
    }

    setAddedOpmes((prev) => [...prev, newOpme])
    setDescription('')
    setQuantity('1')
  }

  const handleDeleteOpme = (opmeCode: string) => {
    setAddedOpmes((prev) => prev.filter((o) => o.code !== opmeCode))
    toast.success('OPME removido com sucesso')
  }

  const handleQuantityChangeOpme = (opmeCode: string, newQuantity: number) => {
    setAddedOpmes((prev) =>
      prev.map((o) =>
        o.code === opmeCode ? { ...o, quantity: newQuantity } : o
      )
    )
  }

  const handleToggleSelectionOpme = (opmeCode: string) => {
    setAddedOpmes((prev) =>
      prev.map((o) =>
        o.code === opmeCode ? { ...o, is_selected: !o.is_selected } : o
      )
    )
  }

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '')

    if (value === '') {
      setQuantity('')
      return
    }

    const numericValue = parseInt(value, 10)
    if (!isNaN(numericValue) && numericValue > 0 && numericValue <= 99) {
      setQuantity(value)
    }
  }

  const handleQuantityBlur = () => {
    if (quantity === '' || parseInt(quantity, 10) <= 0) {
      setQuantity('1')
    }
  }

  const isAddButtonDisabled =
    !description.trim() || !quantity || parseInt(quantity) <= 0
  const isConfirmButtonDisabled = addedOpmes.length === 0

  return (
    <ResponsiveModal
      isOpen={isOpen}
      title="Adicionar OPME"
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmButtonLabel="Salvar e prosseguir"
      disableConfirmButton={isConfirmButtonDisabled}
    >
      {(isMobile) => (
        <>
          <S.FormContainer>
            <S.DescriptionWrapper>
              <TextFieldNew
                name="description"
                label="OPME"
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="textSecondary"
                labelOverlapping
                placeholder="Digite a descrição do OPME"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                requiredColor="textSecondary"
              />
            </S.DescriptionWrapper>

            <S.QuantityWrapper>
              <TextFieldNew
                name="quantity"
                label="Quantidade"
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="textSecondary"
                labelOverlapping
                placeholder="1"
                value={quantity}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                type="number"
                maxLength={2}
                required
                requiredColor="textSecondary"
              />
            </S.QuantityWrapper>
          </S.FormContainer>

          <ButtonNew
            outlined
            size="large"
            fullWidth
            onClick={handleAddOpme}
            disabled={isAddButtonDisabled}
          >
            Adicionar OPME
          </ButtonNew>

          {!!addedOpmes && addedOpmes.length > 0 && (
            <>
              <HeadingNew
                as="h2"
                color="textSecondary"
                weight="bold"
                size="xsmall"
                style={{
                  textAlign: 'start',
                  marginTop: '2rem',
                  marginBottom: '0.5rem'
                }}
              >
                OPMEs adicionados:
              </HeadingNew>

              <ScrollableTableContainer>
                {isMobile ? (
                  <OpmesList
                    initialRows={addedOpmes}
                    onDelete={handleDeleteOpme}
                    onQuantityChange={handleQuantityChangeOpme}
                    onToggleSelection={handleToggleSelectionOpme}
                    hideSections
                  />
                ) : (
                  <OpmesTable
                    initialRows={addedOpmes}
                    onDelete={handleDeleteOpme}
                    onQuantityChange={handleQuantityChangeOpme}
                    onToggleSelection={handleToggleSelectionOpme}
                    hideSections
                  />
                )}
              </ScrollableTableContainer>
            </>
          )}
        </>
      )}
    </ResponsiveModal>
  )
}

export default AddOpmeResponsiveModal
